import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';
import { AUTH } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post('/auth/forgot-password', { email });
            setSent(true);
            toast.success('If the email exists, a reset link was sent.');
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid place-items-center bg-background px-6">
            <div className="w-full max-w-md">
                <div className="mb-8">
                    <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">
                        Reset password
                    </h2>
                    <p className="text-sm text-muted-foreground mt-2">
                        We&apos;ll email you a link. Reset link is also logged to server console for admins.
                    </p>
                </div>
                {sent ? (
                    <div className="rounded-sm border border-border bg-card p-6 text-sm">
                        If <span className="font-mono">{email}</span> matches an account, a reset link is
                        on its way.
                        <div className="mt-4">
                            <Link
                                to="/login"
                                data-testid={AUTH.forgot.backLink}
                                className="text-primary hover:underline"
                            >
                                ← Back to sign in
                            </Link>
                        </div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="femail" className="text-sm">
                                Email
                            </Label>
                            <Input
                                id="femail"
                                data-testid={AUTH.forgot.emailInput}
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="rounded-sm h-11"
                            />
                        </div>
                        <Button
                            type="submit"
                            data-testid={AUTH.forgot.submitButton}
                            disabled={loading}
                            className="w-full h-11 rounded-sm"
                        >
                            {loading ? (
                                <><Loader2 className="animate-spin mr-2" size={16} /> Sending…</>
                            ) : (
                                'Send reset link'
                            )}
                        </Button>
                        <Link
                            to="/login"
                            data-testid={AUTH.forgot.backLink}
                            className="block text-sm text-muted-foreground hover:text-primary"
                        >
                            ← Back to sign in
                        </Link>
                    </form>
                )}
            </div>
        </div>
    );
}
